import {
    Box,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Typography,
} from "@mui/material";
import ResponsiveDialog from "shared/ui/ResponsiveDialog";
import { alpha } from "@mui/material/styles";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import { useTranslation } from "shared/hooks/useTranslation";
import { formatCurrencyAmountOrMasked } from "shared/money/balances";
import type { Operation } from "types/Portfolio";
import { formatNum } from "./investmentUtils";
import { useOfflineDisabled } from "shared/hooks/useOfflineDisabled";

interface OperationHistoryDialogProps {
    open: boolean;
    symbol: string;
    operations: Operation[];
    quoteCurrency: string;
    balancesVisible: boolean;
    onClose: () => void;
    onEdit?: (operation: Operation) => void;
    onDelete?: (operation: Operation) => void;
}

function formatOperationDate(date: string | undefined, language: string): string {
    if (!date) return "-";
    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) return date;
    return new Intl.DateTimeFormat(language.startsWith("es") ? "es-AR" : "en-US", {
        day: "numeric",
        month: "short",
        year: "numeric",
    }).format(parsed);
}

export default function OperationHistoryDialog({
    open,
    symbol,
    operations,
    quoteCurrency,
    balancesVisible,
    onClose,
    onEdit,
    onDelete,
}: OperationHistoryDialogProps) {
    const { t, currentLanguage } = useTranslation();
    const { disabledIfOffline, optionalOfflineTitle } = useOfflineDisabled();
    const mutationsDisabled = disabledIfOffline();

    return (
        <ResponsiveDialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: { sm: 4 },
                    bgcolor: "background.paper",
                    backgroundImage: (dialogTheme) => dialogTheme.palette.mode === "dark"
                        ? `linear-gradient(135deg, ${alpha(dialogTheme.palette.primary.dark, 0.2)}, ${alpha(dialogTheme.palette.background.paper, 0.98)} 62%)`
                        : `linear-gradient(135deg, ${alpha(dialogTheme.palette.primary.main, 0.08)}, ${alpha(dialogTheme.palette.background.paper, 0.99)} 60%)`,
                    border: (dialogTheme) => ({
                        xs: 0,
                        sm: `1px solid ${alpha(dialogTheme.palette.primary.main, dialogTheme.palette.mode === "dark" ? 0.18 : 0.14)}`,
                    }),
                    overflow: "hidden",
                },
            }}
        >
            <DialogTitle sx={{ p: { xs: 2, sm: 2.5 }, pr: 7 }}>
                <Typography component="h2" sx={{ fontWeight: 950, fontSize: { xs: 24, sm: 26 }, lineHeight: 1.1 }}>
                    {t("portfolio.operationsForAsset", { symbol })}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.6, fontWeight: 700 }}>
                    {t("portfolio.operationHistorySubtitle", { count: operations.length })}
                </Typography>
                <IconButton
                    aria-label={t("common.close")}
                    onClick={onClose}
                    sx={{
                        position: "absolute",
                        top: { xs: 12, sm: 16 },
                        right: { xs: 12, sm: 16 },
                        color: "text.secondary",
                        bgcolor: (dialogTheme) => alpha(dialogTheme.palette.text.primary, 0.06),
                    }}
                >
                    <CloseRoundedIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ px: { xs: 2, sm: 2.5 }, pt: 0, pb: { xs: 2, sm: 2.5 } }}>
                {operations.length === 0 ? (
                    <Box
                        sx={{
                            p: 2.5,
                            borderRadius: 3,
                            border: (dialogTheme) => `1px solid ${alpha(dialogTheme.palette.primary.main, 0.14)}`,
                            bgcolor: (dialogTheme) => alpha(dialogTheme.palette.primary.main, dialogTheme.palette.mode === "dark" ? 0.08 : 0.04),
                        }}
                    >
                        <Typography sx={{ fontWeight: 850 }}>
                            {t("portfolio.noOperations")}
                        </Typography>
                    </Box>
                ) : (
                    <Stack spacing={1.15}>
                        {operations.map((operation) => {
                            const total = operation.quantity * operation.price;
                            const canEdit = Boolean(onEdit);
                            const canDelete = Boolean(onDelete);

                            return (
                                <Box
                                    key={operation._id}
                                    sx={{
                                        display: "grid",
                                        gridTemplateColumns: { xs: "44px minmax(0, 1fr)", sm: "48px minmax(0, 1fr) auto" },
                                        gap: 1.35,
                                        alignItems: "center",
                                        p: { xs: 1.35, sm: 1.5 },
                                        borderRadius: 3,
                                        border: (dialogTheme) => `1px solid ${alpha(dialogTheme.palette.text.primary, dialogTheme.palette.mode === "dark" ? 0.1 : 0.08)}`,
                                        bgcolor: (dialogTheme) => alpha(dialogTheme.palette.primary.main, dialogTheme.palette.mode === "dark" ? 0.06 : 0.03),
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: { xs: 44, sm: 48 },
                                            height: { xs: 44, sm: 48 },
                                            borderRadius: "50%",
                                            display: "grid",
                                            placeItems: "center",
                                            color: "primary.main",
                                            bgcolor: (dialogTheme) => alpha(dialogTheme.palette.primary.main, dialogTheme.palette.mode === "dark" ? 0.18 : 0.1),
                                        }}
                                    >
                                        <ReceiptLongRoundedIcon fontSize="small" />
                                    </Box>

                                    <Box sx={{ minWidth: 0 }}>
                                        <Stack direction="row" spacing={1} alignItems="baseline" sx={{ minWidth: 0 }}>
                                            <Typography noWrap sx={{ fontWeight: 950, fontSize: 16 }}>
                                                {t(`portfolio.${operation.side}`)}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 750, flexShrink: 0 }}>
                                                {formatOperationDate(operation.date, currentLanguage)}
                                            </Typography>
                                        </Stack>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.35, fontWeight: 700 }}>
                                            {formatNum(operation.quantity)} {t("portfolio.units")} - {t("portfolio.operationUnitPriceShort")} {formatCurrencyAmountOrMasked(operation.price, operation.currency || quoteCurrency, balancesVisible)}
                                        </Typography>
                                    </Box>

                                    <Box
                                        sx={{
                                            gridColumn: { xs: "1 / -1", sm: "auto" },
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: { xs: "space-between", sm: "flex-end" },
                                            gap: 1,
                                        }}
                                    >
                                        <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
                                            <Typography sx={{ fontWeight: 950, fontVariantNumeric: "tabular-nums" }}>
                                                {formatCurrencyAmountOrMasked(total, operation.currency || quoteCurrency, balancesVisible)}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 750 }}>
                                                {t("portfolio.operationTotalShort")}
                                            </Typography>
                                        </Box>
                                        {(canEdit || canDelete) && (
                                            <Stack direction="row" spacing={0.5}>
                                                {canEdit && (
                                                    <IconButton
                                                        size="small"
                                                        aria-label={t("common.edit")}
                                                        onClick={() => onEdit?.(operation)}
                                                        disabled={mutationsDisabled}
                                                        title={optionalOfflineTitle()}
                                                        sx={{ color: "text.secondary" }}
                                                    >
                                                        <EditOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                )}
                                                {canDelete && (
                                                    <IconButton
                                                        size="small"
                                                        aria-label={t("common.delete")}
                                                        onClick={() => onDelete?.(operation)}
                                                        disabled={mutationsDisabled}
                                                        title={optionalOfflineTitle()}
                                                        sx={{ color: "error.main" }}
                                                    >
                                                        <DeleteOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                )}
                                            </Stack>
                                        )}
                                    </Box>
                                </Box>
                            );
                        })}
                    </Stack>
                )}
            </DialogContent>
        </ResponsiveDialog>
    );
}
